import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, Flame, Activity, ShieldAlert, HeartPulse, Bell,
} from "lucide-react";
import React from "react";

const SEV_COLOR = {
  critical: "hsl(var(--destructive))",
  high:     "hsl(var(--warning))",
  medium:   "hsl(var(--primary))",
  low:      "hsl(var(--muted-foreground))",
};

const SEV_DURATION = {
  critical: 8000,
  high:     6000,
  medium:   4500,
  low:      3500,
};

const iconForType = (type) => {
  if (!type) return Bell;
  if (type.includes("fire") || type.includes("smoke")) return Flame;
  if (type.includes("fall") || type.includes("medical")) return HeartPulse;
  if (type.includes("intrusion") || type.includes("fence") || type.includes("fight")) return ShieldAlert;
  if (type.includes("crowd") || type.includes("stampede") || type.includes("congestion")) return Activity;
  return AlertTriangle;
};

const titleCase = (s) =>
  String(s || "").split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

/**
 * Fires a sonner flash notification whenever a new alert (highest item in `alerts`)
 * is observed. De-dupes by alert.id so existing toasts aren't re-fired on render.
 *
 * Only renders toasts for `medium`, `high`, and `critical` severities by default
 * — `low` events would be too noisy. Adjust via `minSeverity` if needed.
 */
export const useAlertToasts = (alerts, { minSeverity = "medium" } = {}) => {
  const navigate = useNavigate();
  const seenRef = useRef(new Set());
  const bootstrapRef = useRef(false);

  useEffect(() => {
    if (!alerts || alerts.length === 0) return;

    // First render: seed the seen-set with whatever's already there.
    // Otherwise the user gets a flood of toasts for historical alerts.
    if (!bootstrapRef.current) {
      bootstrapRef.current = true;
      alerts.forEach((a) => seenRef.current.add(a.id));
      return;
    }

    const sevRank = { critical: 3, high: 2, medium: 1, low: 0 };
    const minRank = sevRank[minSeverity] ?? 1;

    // Iterate from oldest unseen → newest so toast stack order is intuitive.
    const fresh = alerts.filter((a) => !seenRef.current.has(a.id));
    fresh.reverse().forEach((a) => {
      seenRef.current.add(a.id);
      const rank = sevRank[a.severity] ?? 0;
      if (rank < minRank) return;

      const Icon = iconForType(a.type);
      const color = SEV_COLOR[a.severity] || "hsl(var(--primary))";

      toast.custom(
        (t) => (
          <div
            className="flex items-start gap-2 p-2.5 min-w-[280px] max-w-[360px] border-l-2 cursor-pointer shadow-lg"
            style={{
              borderLeftColor: color,
              background: "hsl(var(--background))",
              border: "1px solid hsl(var(--border))",
              borderLeft: `3px solid ${color}`,
              boxShadow: "0 6px 24px -8px rgba(0,0,0,0.25)",
            }}
            onClick={() => {
              toast.dismiss(t);
              navigate("/incidents");
            }}
            data-testid={`alert-toast-${a.id}`}
          >
            <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color }} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span
                  className="text-[9px] font-bold tracking-wide border px-1"
                  style={{ color, borderColor: color }}
                >
                  {titleCase(a.severity)}
                </span>
                <span className="font-mono text-[9px] text-muted-foreground">
                  {new Date(a.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </span>
              </div>
              <div className="text-[11px] font-medium text-foreground leading-tight mt-1 truncate">
                {a.title}
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1 flex-wrap">
                <span className="truncate">{a.zone_name}</span>
                {a.camera_id && (<><span>·</span><span>{a.camera_id}</span></>)}
                <span className="ml-auto text-[hsl(var(--primary))]">Review →</span>
              </div>
            </div>
          </div>
        ),
        {
          id: a.id,
          duration: SEV_DURATION[a.severity] ?? 4000,
          position: "top-right",
        }
      );
    });

    // Prevent unbounded growth
    if (seenRef.current.size > 500) {
      const keep = new Set(alerts.slice(0, 200).map((a) => a.id));
      seenRef.current = keep;
    }
  }, [alerts, minSeverity, navigate]);
};

export default useAlertToasts;
